import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import { useUser } from '../contexts/UserContext';
import { dishService, specialNameService, getUnifiedItems, nutrientService, tagService } from '../utils/api';
import DeleteDishModal from '../components/DeleteDishModal';
import { containsIgnoringAccents } from '../utils/stringUtils';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// Debounce function to limit how often a function runs
const debounce = (func, wait) => {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
};

const UnifiedTranslationPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const initialTab = location.state?.initialTab || 'dishes';
  const [activeTab, setActiveTab] = useState(initialTab); // 'dishes' or 'specialNames'
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [translations, setTranslations] = useState({});
  const [newItem, setNewItem] = useState({});
  const [nutrientValues, setNutrientValues] = useState({});
  const [newNutrientValues, setNewNutrientValues] = useState({});
  const [editNutrientsCollapsed, setEditNutrientsCollapsed] = useState(false);
  const [createNutrientsCollapsed, setCreateNutrientsCollapsed] = useState(false);
  
  // New state for tags
  const [selectedTags, setSelectedTags] = useState([]);
  const [noTags, setNoTags] = useState(false);
  const [newSelectedTags, setNewSelectedTags] = useState([]);
  const [newNoTags, setNewNoTags] = useState(false);
  const [editTagsCollapsed, setEditTagsCollapsed] = useState(false);
  const [createTagsCollapsed, setCreateTagsCollapsed] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Get language data from Redux
  const currentSchoolId = useSelector((state) => state.language?.currentSchoolId || null);
  const selectedLanguages = useSelector(state => state.language.selectedLanguages || []);
  const defaultLanguageCode = useSelector(state => state.language.defaultLanguageCode || 'en');
  
  // Delete related states - simplified with the new component
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  const queryClient = useQueryClient(); // Get the queryClient for cache invalidation
  
  // Function to properly construct the tag logo URL
  const getTagLogoUrl = useCallback((logoPath) => {
    if (!logoPath) return '';
    
    // If the path already has a protocol, return it as is
    if (logoPath.startsWith('http')) return logoPath;
    
    // Handle UUID format (Nhost storage)
    if (logoPath.includes('-') && !logoPath.includes('/')) {
      return `https://${process.env.REACT_APP_NHOST_SUBDOMAIN || 'gtwosmwmvopoqrodakmd'}.storage.${process.env.REACT_APP_NHOST_REGION || 'eu-central-1'}.nhost.run/v1/files/${logoPath}`;
    }
    
    // For relative paths, prepend API base URL
    if (logoPath.startsWith('/')) {
      return `${window.location.origin}${logoPath}`;
    }
    
    // For legacy path format
    return `${window.location.origin}/api/uploads/logos/${logoPath.split('/').pop()}`;
  }, []);

  // Helper function to get the correct school ID
  const getSchoolId = useCallback(() => {
    // First try to get the school ID from the Redux store (set by SchoolContext from subdomain)
    if (currentSchoolId) {
      // console.log('Using school ID from subdomain context:', currentSchoolId); // Keep console logs minimal
      return currentSchoolId;
    }
    
    // Fall back to user's current school
    if (user?.current_school?.id) {
      // console.log('Using school ID from user context:', user.current_school.id); // Keep console logs minimal
      return user.current_school.id;
    }
    
    console.warn('No school ID available');
    return null;
  }, [currentSchoolId, user]);

  // Fetch enabled nutrients for the current school
  const schoolId = getSchoolId(); // Get schoolId once
  const uiLanguage = i18n.language || 'en';
  const { data: enabledNutrients = [], isLoading: isLoadingNutrients } = useQuery({
    queryKey: ['allNutrients', schoolId, uiLanguage],
    // Use the new function to get all nutrients (both enabled and disabled)
    queryFn: () => nutrientService.listAllNutrients(schoolId, uiLanguage, defaultLanguageCode), 
    enabled: !!schoolId && activeTab === 'dishes', // Only fetch if schoolId exists and tab is 'dishes'
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    onError: (err) => {
      console.error('Failed to fetch nutrients:', err);
      // Optionally set an error state specific to nutrients
    }
  });

  // Fetch tags for the current school
  const { data: tags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: ['tags', schoolId, uiLanguage],
    queryFn: () => tagService.getTags(schoolId, uiLanguage),
    enabled: !!schoolId && activeTab === 'dishes', // Only fetch if schoolId exists and tab is 'dishes'
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    onError: (err) => {
      console.error('Failed to fetch tags:', err);
    }
  });

  // Load user data
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      // console.log('User data from localStorage:', userData.current_school?.id);
      // console.log('School ID from subdomain context:', currentSchoolId);
      setUser(userData);
    } catch (err) {
      setError('Failed to load user data. Please log in again.');
    }
  }, [currentSchoolId]);
  
  // Refactored helper to refresh items using the new endpoint
  const refreshItemsList = useCallback(async () => {
    const currentSchoolId = getSchoolId(); // Renamed local variable to avoid conflict
    if (!currentSchoolId || selectedLanguages.length === 0) return;
    
    const itemType = activeTab === 'dishes' ? 'dish' : 'specialName';
    const currentUiLanguage = i18n.language || 'en'; // Renamed local variable
    const selectedCodes = selectedLanguages.map(l => l.code);
    
    try {
      const data = await getUnifiedItems(currentSchoolId, itemType, currentUiLanguage, selectedCodes);
      setItems(data);
      // Apply search filter immediately
      setFilteredItems(searchQuery.trim() ? data.filter(item => {
        // Fallback display logic for search
        const nameToSearch = item.nameForUI || item.nameForDefault || item.nameInAnySelected || item.nameInAny || '';
        return containsIgnoringAccents(nameToSearch, searchQuery);
      }) : data);
    } catch (err) {
      console.error(`Failed to refresh ${itemType}s:`, err);
    }
  }, [getSchoolId, activeTab, i18n.language, selectedLanguages, searchQuery]); // Added selectedLanguages

  // Handle successful deletion - now defined after refreshItemsList
  const handleDeleteSuccess = useCallback(() => {
    // Show success message
    const itemType = activeTab === 'dishes' ? 'dish' : 'special name';
    setSuccess(`${itemType} deleted successfully!`);
    
    // Invalidate relevant caches
    const currentSchoolId = getSchoolId(); // Make sure schoolId is available
    if (currentSchoolId) {
      queryClient.invalidateQueries({ queryKey: ['nutrientQueue', currentSchoolId] });
      queryClient.invalidateQueries({ queryKey: ['missingTranslations', currentSchoolId] });
    }
    
    // Refresh items list for the current page
    refreshItemsList();
    
    // Clear selection
    setSelectedItemId(null);
    setNutrientValues({}); // Clear nutrient values on delete
  }, [activeTab, refreshItemsList, queryClient, getSchoolId]); // Added queryClient and getSchoolId
  
  // Modified useEffect to fetch items using the new endpoint
  useEffect(() => {
    const fetchItems = async () => {
      const currentSchoolId = getSchoolId(); // Renamed local variable
      if (!currentSchoolId || selectedLanguages.length === 0) { 
        setLoading(false);
        // Optionally show a message if languages aren't loaded yet
        if (currentSchoolId && selectedLanguages.length === 0) {
          console.warn('Selected languages not yet loaded, cannot fetch unified items.');
          setError('Loading language settings...'); // Inform user
        }
        return; 
      }
      
      setLoading(true);
      setError(null);
      setSelectedItemId(null);
      setTranslations({});
      setNutrientValues({}); // Reset nutrient values when fetching items

      const itemType = activeTab === 'dishes' ? 'dish' : 'specialName';
      const currentUiLanguage = i18n.language || 'en'; // Renamed local variable
      const selectedCodes = selectedLanguages.map(l => l.code);

      try {
        const data = await getUnifiedItems(currentSchoolId, itemType, currentUiLanguage, selectedCodes);
        setItems(data);
        setFilteredItems(data);
      } catch (err) {
        console.error(`Failed to fetch unified ${itemType}s:`, err);
        setError(`Failed to load ${itemType}s. Please try again later.`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchItems();
    // Dependencies: fetch when user, language, tab, schoolId, or selectedLanguages change
  }, [user, i18n.language, activeTab, currentSchoolId, getSchoolId, selectedLanguages]); 

  // Load translations and nutrient values for selected item - Refactored
  useEffect(() => {
    const loadItemDetails = async () => {
      console.log(`[LoadDetails] Loading details for selected item ID: ${selectedItemId}, items count: ${items?.length}`);
      
      if (!selectedItemId) {
        setTranslations({});
        setNutrientValues({});
        setSelectedTags([]);  // Reset tags
        setNoTags(false);     // Reset no tags flag
        return;
      }
      
      // Reset edit nutrients collapsed state when selecting a new item
      setEditNutrientsCollapsed(false);
      setEditTagsCollapsed(false); // Reset tags collapsed state
      
      // Find the selected item from the *current* items list
      const selectedItemData = items.find(item => item.id === selectedItemId);
      console.log(`[LoadDetails] Selected item data found: ${!!selectedItemData}`);
      
      if (selectedItemData) {
        // Set translations
        setTranslations(selectedItemData.allSelectedTranslations || {});
        
        // Fetch nutrient values if this is a dish (not a special name)
        if (activeTab === 'dishes') {
          try {
            // Add timeout handling for nutrient fetch
            const nutrientPromise = dishService.getDishNutrients(selectedItemId);
            
            const normalizedValues = {};
            
            // Create a timeout promise
            const nutrientTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Nutrient fetch timed out')), 10000)
            );
              
            // Race between the fetch and timeout
            const nutrientResponse = await Promise.race([nutrientPromise, nutrientTimeout])
              .catch(async (err) => {
                console.warn(`[LoadDetails] Nutrient fetch failed or timed out: ${err.message}, retrying...`);
                // Wait 1s and try again
                await new Promise(resolve => setTimeout(resolve, 1000));
                return dishService.getDishNutrients(selectedItemId);
              });
              
            // Process the nutrient values
            if (nutrientResponse && nutrientResponse.nutrients) {
              nutrientResponse.nutrients.forEach(n => {
                normalizedValues[n.nutrient_id] = n.value;
              });
              console.log(`[LoadDetails] Normalized nutrient values:`, normalizedValues);
            } else {
              console.log(`[LoadDetails] No nutrients found in response for dish ${selectedItemId}`);
            }
            
            setNutrientValues(normalizedValues);
          } catch (err) {
            console.error('[LoadDetails] Failed to fetch nutrient values:', err);
            // Optional error handling for nutrient fetch failures
          }
          
          // Fetch tags for the dish
          try {
            console.log(`[LoadDetails] Fetching tags for dish ID: ${selectedItemId}`);
            
            // Add timeout handling for tag fetch
            const tagPromise = tagService.getDishTags(selectedItemId, uiLanguage);
            
            // Create a timeout promise
            const tagTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Tag fetch timed out')), 10000)
            );
            
            // Race between the fetch and timeout
            const tagResponse = await Promise.race([tagPromise, tagTimeout])
              .catch(async (err) => {
                console.warn(`[LoadDetails] Tag fetch failed or timed out: ${err.message}, retrying...`);
                // Wait 1s and try again
                await new Promise(resolve => setTimeout(resolve, 1000));
                return tagService.getDishTags(selectedItemId, uiLanguage);
              });
            
            console.log(`[LoadDetails] Tag response:`, tagResponse);
            
            if (tagResponse) {
              if (tagResponse.no_tags) {
                setNoTags(true);
                setSelectedTags([]);
              } else {
                setNoTags(false);
                setSelectedTags(tagResponse.tags.map(a => a.id) || []);
              }
            }
          } catch (err) {
            console.error('[LoadDetails] Failed to fetch tags for dish:', err);
            // Continue without tag data
            setNoTags(false);
            setSelectedTags([]);
          }
        }
      } else {
        console.warn(`[LoadDetails] Selected item ID ${selectedItemId} not found in items list!`);
      }
    };
    
    loadItemDetails();
  }, [selectedItemId, items, activeTab, uiLanguage]);
  
  // Filter items when search query or items change - Modified search logic
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems(items);
      return;
    }
    
    const filtered = items.filter(item => {
      // Use the structured name fields for searching
      const nameToSearch = item.nameForUI || item.nameForDefault || item.nameInAnySelected || item.nameInAny || '';
      return containsIgnoringAccents(nameToSearch, searchQuery);
    });
    
    setFilteredItems(filtered);
  }, [searchQuery, items]);

  // Debounced search handler - fixed the inline function issue
  const handleSearchChange = useCallback(
    (value) => {
      const debouncedSearch = debounce((searchValue) => {
        setSearchQuery(searchValue);
      }, 300);
      debouncedSearch(value);
    },
    []
  );
  
  // Handle saving - Use the existing dish/specialName update endpoints
  const handleSaveItem = async () => {
    if (!selectedItemId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    // Get necessary IDs and languages
    const currentSchoolId = getSchoolId(); // Renamed local variable
    const currentUiLanguage = i18n.language || 'en'; // Renamed local variable
    const selectedCodesString = selectedLanguages.map(l => l.code).join(',');
    
    try {
      const itemType = activeTab === 'dishes' ? 'dish' : 'specialName';
      const service = activeTab === 'dishes' ? dishService : specialNameService;
      
      // Prepare translations payload
      const translationsPayload = Object.entries(translations)
        .map(([code, name]) => ({ language_code: code, name: name || '' }));
      
      // Data for the PUT request body
      const updateData = { translations: translationsPayload };

      // Add nutrient values if it's a dish
      if (activeTab === 'dishes') {
        // Convert values to numbers, defaulting invalid/empty inputs to null or 0? Plan uses ON CONFLICT UPDATE, so sending existing keys is fine.
        // Backend check `value >= 0`, so let's filter out invalid numbers client-side.
        const validNutrientValues = {};
        for (const [key, value] of Object.entries(nutrientValues)) {
          const numValue = parseFloat(value);
          // Only include valid, non-negative numbers
          if (!isNaN(numValue) && numValue >= 0) { 
            validNutrientValues[key] = numValue;
          } else if (value === '' || value === null) {
             // Decide how to handle removal - send null? Or rely on backend to ignore invalid? 
             // Plan says: "Handle potential removal if a value is set to null/empty" - let's send the key with null?
             // For now, let's just send valid numbers. Backend needs to handle missing keys if they mean "delete".
             // Let's assume the backend handles `ON CONFLICT DO UPDATE` and only updates provided values.
             // To clear a value, maybe we need a specific endpoint or pass `null`?
             // Revisit this: For now, only send valid numbers.
             // validNutrientValues[key] = null; // Option to explicitly clear
          } else {
            console.warn(`Invalid nutrient value skipped for nutrient ID ${key}: ${value}`);
          }
        }
        updateData.nutrientValues = validNutrientValues;
      }
      
      // Call the correct update method with language params
      let responseData;
      if (activeTab === 'dishes') {
        responseData = await service.updateDish(selectedItemId, updateData, selectedCodesString, currentUiLanguage);
      } else {
        responseData = await service.updateSpecialName(selectedItemId, updateData, selectedCodesString, currentUiLanguage); 
      }
      
      // --- Cache Updates --- 
      
      // Handle new completion checker response format
      if (responseData?.queueUpdates) {
        console.log(`[UnifiedSave] Processing queue updates:`, responseData.queueUpdates);
        
        // Update translation queue if completion status changed
        if (responseData.queueUpdates.removeFromTranslations) {
          console.log(`[UnifiedSave] Removing item ${selectedItemId} from translation queue`);
          const missingTranslationsQueryKey = ['missingTranslations', currentSchoolId, selectedCodesString];
          queryClient.setQueryData(missingTranslationsQueryKey, (oldData) => 
            oldData?.filter(item => item.id !== selectedItemId) || []
          );
          
          // Also invalidate query without selectedCodesString for backwards compatibility
          queryClient.invalidateQueries({ queryKey: ['missingTranslations', currentSchoolId] });
        }
        
        // Update nutrient queue if completion status changed
        if (responseData.queueUpdates.removeFromNutrients) {
          console.log(`[UnifiedSave] Removing item ${selectedItemId} from nutrient queue`);
          queryClient.setQueryData(['nutrientQueue', currentSchoolId], (oldData) => 
            oldData?.filter(item => item.id !== selectedItemId) || []
          );
        }
        
        // Update tag queue if completion status changed
        if (responseData.queueUpdates.removeFromTags) {
          console.log(`[UnifiedSave] Removing item ${selectedItemId} from tag queue`);
          queryClient.setQueryData(['missingTags', currentSchoolId], (oldData) => 
            oldData?.filter(item => item.id !== selectedItemId) || []
          );
        }
        
        console.log(`[UnifiedSave] Cache updated with completion status`);
      } else if (responseData && responseData.updatedMissingItems && Array.isArray(responseData.updatedMissingItems)) {
        // Fallback: handle old response format for backwards compatibility
        const missingTranslationsQueryKey = ['missingTranslations', currentSchoolId, selectedCodesString];
        console.log(`[UnifiedSave - Cache Update] Updating ${JSON.stringify(missingTranslationsQueryKey)} with ${responseData.updatedMissingItems.length} items from API response (fallback mode)`);
        queryClient.setQueryData(missingTranslationsQueryKey, responseData.updatedMissingItems);
      } else {
        console.warn('[UnifiedSave - Cache Update] API response did not contain expected queueUpdates or updatedMissingItems. Missing translations cache not updated.');
      }
      
      // 2. Invalidate other caches relevant to this page
      console.log(`[UnifiedSave] Invalidating related caches: dishes, specialNames, item ${selectedItemId}, potentially nutrient queue`);
      await queryClient.invalidateQueries({ queryKey: ['dishes'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['dish', selectedItemId], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['specialNames'], refetchType: 'all' });
      await queryClient.invalidateQueries({ queryKey: ['specialName', selectedItemId], refetchType: 'all' });
      // Invalidate nutrient queue if a dish was updated
      if (activeTab === 'dishes') {
        await queryClient.invalidateQueries({ queryKey: ['nutrientQueue', currentSchoolId], refetchType: 'all' });
        // --- ADD INVALIDATION FOR PUBLIC MENU HERE ---
        const subdomain = user?.current_school?.subdomain; 
        if (subdomain) {
          console.log(`[UnifiedSave] Invalidating public menu cache for subdomain: ${subdomain}`);
          await queryClient.invalidateQueries({ queryKey: ['publicMenu', subdomain], refetchType: 'all' });
        } else {
          console.warn('[UnifiedSave] Could not invalidate public menu cache: subdomain not found.');
        }
      }

      // Save tag values
      // Auto-fix: If no tags are selected and "no tags" is not checked, automatically set "no tags" to true
      let shouldSaveNoTags = noTags;
      let tagsToSave = selectedTags;
      
      if (!noTags && selectedTags.length === 0) {
        // User unchecked all tags but forgot to check "no tags" - automatically handle this
        shouldSaveNoTags = true;
        console.log('[UnifiedSave] Auto-setting "no tags" to true since no individual tags are selected');
      }
      
      if (shouldSaveNoTags) {
        await tagService.updateDishTags(selectedItemId, [], true);
      } else if (tagsToSave.length > 0) {
        await tagService.updateDishTags(selectedItemId, tagsToSave, false);
      }

      setSuccess(`${itemType} updated successfully!`);
      console.log(`[UnifiedSave] Calling refreshItemsList after successful save and cache updates at ${new Date().toISOString()}`);
      refreshItemsList(); // Refresh the list for this page
    } catch (err) {
      console.error('Error saving item:', err);
      const errorMessage = err.message || '';
      // More robust check for 409 / Duplicate error
      if (errorMessage.includes('409') || errorMessage.toLowerCase().includes('duplicate')) { // Broadened check
        let displayMessage = 'Duplicate entry detected.'; // Default message
        let displayLang = '';

        try {
          // Try parsing the whole message as JSON if it looks like it
          if (errorMessage.startsWith('{') && errorMessage.endsWith('}')) {
             const errorData = JSON.parse(errorMessage);
             displayMessage = errorData.message || displayMessage;
             displayLang = errorData.language || '';
          } else {
             // Otherwise, try regex extraction from plain text message
             const msgMatch = errorMessage.match(/message:\s*\"?([^\"]+.*?)\"?($|,\s*\"|\s*})/i);
             if (msgMatch && msgMatch[1]) {
                 displayMessage = msgMatch[1].trim();
             }
             const langMatch = errorMessage.match(/language:\s*\"?([a-z]{2})\"?/i);
             if (langMatch && langMatch[1]) {
                 displayLang = langMatch[1].toUpperCase();
             }
          }
        } catch (parseError) {
           console.warn('Could not parse duplicate error details:', parseError);
           // Use default message if parsing fails
        }
        
        setError(`Error: ${displayMessage}${displayLang ? ` Please check the translation for ${displayLang}.` : ''}`);
      } else {
        setError(`Error saving ${activeTab === 'dishes' ? 'dish' : 'special name'}: ${errorMessage || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Handle creating new item - Use existing endpoints
  const handleNewItemSubmit = async (e) => {
    e.preventDefault();
    
    const currentSchoolId = getSchoolId();
    if (!currentSchoolId) {
      setError('No school selected');
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const translationsPayload = Object.entries(newItem)
        .filter(([_, name]) => name && name.trim() !== '')
        .map(([code, name]) => ({ language_code: code, name: name.trim() }));

      if (translationsPayload.length === 0) {
         setError('At least one translation is required.');
         setSaving(false);
         return;
      }
      
      const data = { schoolId: currentSchoolId, translations: translationsPayload };

      if (activeTab === 'dishes') {
        const validNutrientValues = {};
        for (const [key, value] of Object.entries(newNutrientValues)) {
           const numValue = parseFloat(value);
           if (!isNaN(numValue) && numValue >= 0) {
             validNutrientValues[key] = numValue;
           }
        }
        if (Object.keys(validNutrientValues).length > 0) {
          data.nutrientValues = validNutrientValues;
        }
      }
      
      let result;
      if (activeTab === 'dishes') {
        console.log('[Create] Creating dish with nutrient values:', data.nutrientValues);
        result = await dishService.createDish(data);
        console.log('[Create] Dish created successfully with ID:', result?.id);
        
        if (result && result.id) {
          // Auto-fix: If no tags are selected and "no tags" is not checked, automatically set "no tags" to true
          let shouldSaveNoTags = newNoTags;
          let tagsToSave = newSelectedTags;
          
          if (!newNoTags && newSelectedTags.length === 0) {
            // User unchecked all tags but forgot to check "no tags" - automatically handle this
            shouldSaveNoTags = true;
            console.log('[UnifiedCreate] Auto-setting "no tags" to true since no individual tags are selected');
          }
          
          if (shouldSaveNoTags) {
            await tagService.updateDishTags(result.id, [], true);
          } else if (tagsToSave.length > 0) {
            await tagService.updateDishTags(result.id, tagsToSave, false);
          }
        }
      } else {
        result = await specialNameService.createSpecialName(data);
      }
      
      setSuccess(`${activeTab === 'dishes' ? 'Dish' : 'Special name'} created successfully!`);
      setNewItem({});
      setNewNutrientValues({});
      setNewSelectedTags([]);
      setNewNoTags(false);
      
      // Construct the query key for getUnifiedItems
      const itemTypeForQuery = activeTab === 'dishes' ? 'dish' : 'specialName';
      const selectedCodesString = selectedLanguages.map(l => l.code).join(',');
      const unifiedItemsQueryKey = ['unifiedItems', currentSchoolId, itemTypeForQuery, uiLanguage, selectedCodesString];

      // Invalidate the specific unifiedItems query first to ensure it refetches with new data
      console.log(`[UnifiedCreate] Invalidating specific unifiedItems query: ${JSON.stringify(unifiedItemsQueryKey)}`);
      await queryClient.invalidateQueries({ queryKey: unifiedItemsQueryKey, refetchType: 'active' });

      // Then refresh the list on the page (which uses getUnifiedItems internally)
      console.log('[Create] Refreshing items list before selecting new dish');
      await refreshItemsList(); 
      
      // Wait for a tick to ensure the items state is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Double check if the newly created dish exists in the items array
      const newDishExists = result?.id && items.some(item => item.id === result.id);
      console.log(`[Create] New dish ID ${result?.id} exists in items array: ${newDishExists}`);
      
      // Invalidate other related general caches as before
      queryClient.invalidateQueries({ queryKey: ['missingTranslations'], refetchType: 'all' });
      
      if (activeTab === 'dishes') {
        queryClient.invalidateQueries({ queryKey: ['nutrientQueue', currentSchoolId], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: ['dishesNeedingTags', currentSchoolId, uiLanguage], refetchType: 'all'});
        
        // Ensure we have the nutrient values for the newly created dish
        if (result?.id) {
          try {
            console.log(`[Create] Explicitly fetching nutrient values for new dish ${result.id}`);
            const nutrientResponse = await dishService.getDishNutrients(result.id);
            console.log('[Create] Nutrient response for new dish:', nutrientResponse);
          } catch (err) {
            console.error('[Create] Failed to pre-fetch nutrient values for new dish:', err);
          }
        }
      }

      if (result?.id) {
        console.log(`[Create] Setting selectedItemId to new dish ID: ${result.id}`);
        
        // After item creation and refreshing the items list, we should ensure the new item 
        // is properly loaded in the list before setting it as selected.
        // This prevents the issue where nutrient values aren't loaded because the dish
        // hasn't been fully propagated to the state yet.
        const retryCount = 5;
        let retryIndex = 0;
        let foundNewItem = false;
        
        // Poll to make sure the item is in the list before selecting it
        while (retryIndex < retryCount && !foundNewItem) {
          console.log(`[Create] Checking if dish ${result.id} exists in items list (attempt ${retryIndex + 1}/${retryCount})`);
          
          // Check if the newly created item exists in the items array
          foundNewItem = items.some(item => item.id === result.id);
          
          if (foundNewItem) {
            console.log(`[Create] Found dish ${result.id} in items list, setting as selected`);
          } else {
            console.log(`[Create] Dish ${result.id} not yet in items list, waiting...`);
            // Wait a bit before checking again
            await new Promise(resolve => setTimeout(resolve, 200));
            // Refresh items list again if needed
            if (retryIndex === 1) { // Try refreshing one more time after the first failure
              console.log(`[Create] Refreshing items list again to find new dish ${result.id}`);
              await queryClient.invalidateQueries({ queryKey: unifiedItemsQueryKey, refetchType: 'active' });
              await refreshItemsList();
            }
          }
          
          retryIndex++;
        }
        
        if (foundNewItem) {
          setSelectedItemId(result.id);
        } else {
          console.warn(`[Create] Could not find the newly created dish ${result.id} in items list after ${retryCount} attempts`);
          // Try setting it anyway - the next refresh cycle might pick it up
          setSelectedItemId(result.id);
          // Force additional refresh after setting the ID to ensure it's loaded
          setTimeout(() => {
            refreshItemsList();
          }, 500);
        }
      }

    } catch (err) {
      console.error(`Error creating ${activeTab === 'dishes' ? 'dish' : 'special name'}:`, err);
      const errorMessage = err.message || '';
      if (errorMessage.includes('409') || errorMessage.toLowerCase().includes('duplicate')) { 
        let displayMessage = 'Duplicate entry detected.';
        let displayLang = '';
        try {
           if (errorMessage.startsWith('{') && errorMessage.endsWith('}')) {
               const errorData = JSON.parse(errorMessage);
               displayMessage = errorData.message || displayMessage;
               displayLang = errorData.language || '';
           } else {
               const msgMatch = errorMessage.match(/message:\s*"?([^"].*?)"?($|,\s*"|\s*})/i);
               if (msgMatch && msgMatch[1]) {
                   displayMessage = msgMatch[1].trim();
               }
               const langMatch = errorMessage.match(/language:\s*"?([a-z]{2})"?/i);
               if (langMatch && langMatch[1]) {
                   displayLang = langMatch[1].toUpperCase();
               }
           }
        } catch (parseError) {
           console.warn('Could not parse duplicate error details:', parseError);
        }
        setError(`Error: ${displayMessage}${displayLang ? ` Please check the translation for ${displayLang}.` : ''}`);
      } else {
        setError(`Error creating ${activeTab === 'dishes' ? 'dish' : 'special name'}: ${errorMessage || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Handle translation change
  const handleTranslationChange = (langCode, value) => {
    setTranslations(prev => ({
      ...prev,
      [langCode]: value
    }));
  };
  
  // Handle new item change
  const handleNewItemChange = (langCode, value) => {
    setNewItem(prev => ({
      ...prev,
      [langCode]: value
    }));
  };

  // Handle nutrient value change for existing item
  const handleNutrientValueChange = (nutrientId, value) => {
    setNutrientValues(prev => ({
      ...prev,
      [nutrientId]: value // Store as string temporarily, validate on save
    }));
  };

  // Handle nutrient value change for new item
  const handleNewNutrientValueChange = (nutrientId, value) => {
    setNewNutrientValues(prev => ({
      ...prev,
      [nutrientId]: value // Store as string temporarily, validate on save
    }));
  };
  
  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setSelectedItemId(null);
      setTranslations({});
      setNutrientValues({});
      setSelectedTags([]);
      setNoTags(false);
      setNewItem({});
      setNewNutrientValues({});
      setNewSelectedTags([]);
      setNewNoTags(false);
      setEditNutrientsCollapsed(false);
      setCreateNutrientsCollapsed(false);
      setEditTagsCollapsed(false);
      setCreateTagsCollapsed(false);
      setError(null);
      setSuccess(null);
      setSearchQuery('');
    }
  };
  
  // Refactored getDisplayName to use new structured data
  const getDisplayName = useCallback((item) => {
    if (!item) {
      return { name: t('admin.noTranslation'), isFallback: true };
    }

    // 1. Use UI Language name if available
    if (item.nameForUI) {
      return { name: item.nameForUI, isFallback: false };
    }
    // 2. Use School Default Language name if available
    if (item.nameForDefault) {
      const defaultLang = selectedLanguages.find(l => l.code === defaultLanguageCode);
      return { 
        name: item.nameForDefault, 
        isFallback: true, 
        // Use the code directly and uppercase it
        language: (defaultLang?.code || defaultLanguageCode).toUpperCase() 
      };
    }
    // 3. Use name from any other *Selected* Language if available
    if (item.nameInAnySelected) {
      const fallbackLangCode = Object.entries(item.allSelectedTranslations || {}).find(([_, name]) => name === item.nameInAnySelected)?.[0];
      return { 
        name: item.nameInAnySelected, 
        isFallback: true, 
        // Use the code directly and uppercase it
        language: (fallbackLangCode || '??').toUpperCase()
      };
    }
    // 4. Use name from *Any* available language (shouldn't happen if selectedLanguages is comprehensive)
    if (item.nameInAny) {
       console.warn(`Item ${item.id}: Falling back to a non-selected language name.`);
       const fallbackLangCode = Object.entries(item.allSelectedTranslations || {}).find(([_, name]) => name === item.nameInAny)?.[0];
       return { 
        name: item.nameInAny, 
        isFallback: true, 
        // Use the code directly and uppercase it
        language: (fallbackLangCode || '??').toUpperCase()
      };
    }
    // 5. Absolute fallback
    return { name: t('admin.noTranslation'), isFallback: true };

  }, [t, defaultLanguageCode, selectedLanguages]); // Added dependencies
  
  // Open delete modal
  const openDeleteModal = () => {
    if (!selectedItemId) return;
    setDeleteModalOpen(true);
  };
  
  // Handle tag selection change
  const handleTagChange = (tagId) => {
    setSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
    setNoTags(false); // If selecting an tag, turn off "no tags"
  };
  
  // Handle new tag selection change
  const handleNewTagChange = (tagId) => {
    setNewSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
    setNewNoTags(false); // If selecting an tag, turn off "no tags"
  };
  
  // Handle "no tags" checkbox
  const handleNoTagsChange = (checked) => {
    setNoTags(checked);
    if (checked) {
      setSelectedTags([]); // Clear selected tags when "no tags" is checked
    }
  };
  
  // Handle "no tags" checkbox for new dish
  const handleNewNoTagsChange = (checked) => {
    setNewNoTags(checked);
    if (checked) {
      setNewSelectedTags([]); // Clear selected tags when "no tags" is checked
    }
  };

  return (
    <>
      <Navbar isAdmin={true} />
      <div className="min-h-screen bg-gray-100">
        <div className="container mx-auto py-8 px-4">
          <h1 className="text-3xl font-bold mb-6">{t('admin.translationEditor')}</h1>
          
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            {/* Tab Navigation */}
            <div className="border-b">
              <div className="flex">
                <button
                  className={`px-6 py-3 text-sm font-medium transition-all duration-200 ease-in-out ${
                    activeTab === 'dishes'
                      ? 'text-blue-600 border-b-2 border-blue-600 font-semibold'
                      : 'text-gray-500 hover:text-blue-600 hover:border-blue-200 border-b-2 border-transparent'
                  }`}
                  onClick={() => handleTabChange('dishes')}
                >
                  {t('Dishes')}
                </button>
                <button
                  className={`px-6 py-3 text-sm font-medium transition-all duration-200 ease-in-out ${
                    activeTab === 'specialNames'
                      ? 'text-blue-600 border-b-2 border-blue-600 font-semibold'
                      : 'text-gray-500 hover:text-blue-600 hover:border-blue-200 border-b-2 border-transparent'
                  }`}
                  onClick={() => handleTabChange('specialNames')}
                >
                  {t('Special Names')}
                </button>
              </div>
            </div>
          
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 mx-2 mt-2" role="alert">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 mx-2 mt-2" role="alert">
                {success}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3">
              {/* Left column: Items list */}
              <div className="bg-white p-4 border-r border-gray-200 md:border-b-0 border-b">
                <h2 className="text-xl font-semibold mb-4">
                  {activeTab === 'dishes' ? t('Dishes') : t('Special Names')}
                </h2>
                
                {/* Search bar */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder={t('Search...')}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                </div>
                
                {loading && !items.length ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <div className="max-h-[30rem] overflow-y-auto">
                    {filteredItems.length > 0 ? (
                      <ul className="divide-y">
                        {filteredItems.map(item => {
                          // Use the refactored function
                          const displayInfo = getDisplayName(item); 
                          return (
                            <li key={item.id} className="py-2">
                              <button
                                className={`w-full text-left px-3 py-2 rounded transition-colors duration-150 ${selectedItemId === item.id ? 'bg-blue-100 text-blue-800 font-medium' : 'hover:bg-gray-100'}`}
                                onClick={() => setSelectedItemId(item.id)}
                              >
                                {displayInfo.isFallback ? (
                                  <div className="flex items-center">
                                    <span className="text-gray-600 italic">{displayInfo.name}</span>
                                    {displayInfo.language && (
                                      <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded-full">
                                        {displayInfo.language}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  displayInfo.name
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : searchQuery ? (
                      <p className="text-gray-500 p-3">
                        {t('No results match your search')}
                      </p>
                    ) : (
                      <p className="text-gray-500 p-3">
                        {activeTab === 'dishes' 
                          ? t('admin.noDishes') 
                          : t('admin.noSpecialNames')}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Middle column: Edit translations and nutrients */}
              <div className="bg-white p-4 border-r border-gray-200 md:border-b-0 border-b">
                <h2 className="text-xl font-semibold mb-4">{t('admin.editTranslations')}</h2>
                
                {selectedItemId ? (
                  <div className="pr-2">
                    <div className="max-h-[28rem] overflow-y-auto">
                    {/* Translation Inputs */}
                    <div className="space-y-4 mb-6">
                      <h3 className="text-lg font-medium text-gray-700 border-b pb-1 mb-3">{t('Translations')}</h3>
                      {selectedLanguages.length > 0 ? selectedLanguages.map(lang => (
                        <div key={lang.code} className="mb-4">
                          <label htmlFor={`edit-trans-${lang.code}`} className="block text-gray-700 text-sm font-bold mb-1">
                            {lang.name} ({lang.code}): 
                          </label>
                          <input
                            id={`edit-trans-${lang.code}`}
                            type="text"
                            value={translations[lang.code] || ''} 
                            onChange={(e) => handleTranslationChange(lang.code, e.target.value)}
                            placeholder={t('admin.enterTranslation')}
                            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )) : <p className="text-gray-500 italic">{t('settings.noLanguagesConfigured')}</p>}
                    </div>

                    {/* Nutrient Inputs (only for dishes) */}
                    {activeTab === 'dishes' && (
                      <div className="space-y-4 mb-6 mt-6 pt-4 border-t">
                        <div 
                          className="flex items-center cursor-pointer mb-2 border-b pb-1" 
                          onClick={() => setEditNutrientsCollapsed(prev => !prev)}
                        >
                          <span className="text-gray-700 mr-2 transition-transform duration-200">
                            {editNutrientsCollapsed ? '▶' : '▼'}
                          </span>
                          <h3 className="text-lg font-medium text-gray-700 flex-grow">
                            {t('Nutrient Values')}
                          </h3>
                        </div>
                        
                        {!editNutrientsCollapsed && (
                          <>
                            {isLoadingNutrients ? (
                              <p className="text-gray-500 italic">{t('Loading nutrients...')}</p>
                            ) : enabledNutrients.length > 0 ? (
                              enabledNutrients.map(nutrient => (
                                <div key={nutrient.id} className="mb-4">
                                  <label htmlFor={`edit-nutrient-${nutrient.id}`} className="block text-gray-700 text-sm font-bold mb-1">
                                    {nutrient.isFallback ? (
                                      <span className="italic font-normal text-gray-600">
                                        {nutrient.name}
                                        {nutrient.fallbackLanguage && 
                                          <span className="ml-1">({nutrient.fallbackLanguage})</span>
                                        }
                                      </span>
                                    ) : (
                                      nutrient.name
                                    )}
                                    {/* Display unit in parentheses */}
                                    {nutrient.unit && (
                                      <span className="text-xs ml-1 text-gray-500">({nutrient.unit})</span>
                                    )}
                                    {/* Add disabled indicator */}
                                    {!nutrient.isEnabled && (
                                      <span className="ml-2 text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full">
                                        {t('Disabled')}
                                      </span>
                                    )}
                                  </label>
                                  <input
                                    id={`edit-nutrient-${nutrient.id}`}
                                    type="number"
                                    step="any" 
                                    min="0"
                                    value={nutrientValues[nutrient.id] ?? ''} 
                                    onChange={(e) => handleNutrientValueChange(nutrient.id, e.target.value)}
                                    // Disable scroll wheel changing value
                                    onWheel={(e) => e.target.blur()}
                                    placeholder={t('Enter numeric value')}
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              ))
                            ) : (
                              <p className="text-gray-500 italic">{t('No nutrients found for this school, or nutrients cannot be edited here.')}</p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Tag section - Only show for dishes */}
                    {activeTab === 'dishes' && (
                      <div className="space-y-4 mb-6 mt-6 pt-4 border-t">
                        <div 
                          className="flex items-center cursor-pointer mb-2 border-b pb-1" 
                          onClick={() => setEditTagsCollapsed(prev => !prev)}
                        >
                          <span className="text-gray-700 mr-2 transition-transform duration-200">
                            {editTagsCollapsed ? '▶' : '▼'}
                          </span>
                          <h3 className="text-lg font-medium text-gray-700 flex-grow">
                            {t('Tags')}
                          </h3>
                        </div>
                        
                        {!editTagsCollapsed && (
                          <div className="mt-2">
                            <div className="mb-4">
                              <label className="flex items-center">
                                <input 
                                  type="checkbox" 
                                  checked={noTags}
                                  onChange={(e) => handleNoTagsChange(e.target.checked)}
                                  className="mr-2 h-4 w-4 text-blue-600"
                                />
                                <span>{t('No Tags in this Dish')}</span>
                              </label>
                            </div>
                            
                            {!noTags && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {isLoadingTags ? (
                                  <p>{t('Loading tags...')}</p>
                                ) : tags.length === 0 ? (
                                  <p className="text-amber-600">{t('No tags configured. Add tags in the Settings page first.')}</p>
                                ) : (
                                  tags.map(tag => (
                                    <label key={tag.id} className="flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={selectedTags.includes(tag.id)}
                                        onChange={() => handleTagChange(tag.id)}
                                        className="mr-2 h-4 w-4 text-blue-600 flex-shrink-0"
                                        disabled={noTags}
                                      />
                                      <span className="flex items-center">
                                        {tag.logo_path && (
                                          <img 
                                            src={getTagLogoUrl(tag.logo_path)} 
                                            alt={tag.name}
                                            className="h-6 w-6 mr-2 object-contain flex-shrink-0"
                                          />
                                        )}
                                        <span className="text-sm">{tag.name}</span>
                                      </span>
                                    </label>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                    <div className="space-y-2 mt-4">
                      <button
                        onClick={handleSaveItem}
                        disabled={saving || loading || isLoadingNutrients} // Disable while nutrients are loading
                        className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? t('saving') : t('admin.saveChanges')} 
                      </button>
                      
                      <button
                        onClick={openDeleteModal}
                        disabled={loading || saving} // Keep disabled if loading items or saving
                        className="w-full bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                      >
                        {loading ? t('admin.deleting') : t('admin.delete')} {activeTab === 'dishes' ? t('Dish') : t('Special Name')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic p-3">
                    {t('Select an item from the list to edit its translations.')}
                     {activeTab === 'dishes' && t(' Nutrient values can also be edited here.')}
                  </p>
                )}
              </div>
              
              {/* Right column: Create new item */}
              <div className="bg-white p-4">
                <h2 className="text-xl font-semibold mb-4">
                  {activeTab === 'dishes' 
                    ? t('admin.createDish') 
                    : t('admin.createSpecialName')}
                </h2>
                
                <form onSubmit={handleNewItemSubmit}>
                  <div className="max-h-[30rem] overflow-y-auto pr-2">
                    {/* New Item Translation Inputs */}
                    <div className="space-y-4 mb-6">
                      <h3 className="text-lg font-medium text-gray-700 border-b pb-1 mb-3">{t('Translations')}</h3>
                      {selectedLanguages.length > 0 ? selectedLanguages.map(lang => (
                        <div key={lang.code} className="mb-4">
                          <label htmlFor={`new-trans-${lang.code}`} className="block text-gray-700 text-sm font-bold mb-1">
                            {lang.name} ({lang.code}): 
                            {/* Add asterisk if this is the default language */} 
                            {lang.code === defaultLanguageCode && <span className="text-red-500 ml-1">*</span>} 
                          </label>
                          <input
                            id={`new-trans-${lang.code}`}
                            type="text"
                            value={newItem[lang.code] || ''} 
                            onChange={(e) => handleNewItemChange(lang.code, e.target.value)}
                            placeholder={t('admin.enterTranslation')}
                            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            // Mark as required only for the default language
                            required={lang.code === defaultLanguageCode} 
                          />
                        </div>
                      )) : <p className="text-gray-500 italic">{t('settings.noLanguagesConfigured')}</p>}
                    </div>

                    {/* New Item Nutrient Inputs (only for dishes) */}
                    {activeTab === 'dishes' && (
                      <div className="space-y-4 mb-6 mt-6 pt-4 border-t">
                        <div 
                          className="flex items-center cursor-pointer mb-2 border-b pb-1" 
                          onClick={() => setCreateNutrientsCollapsed(prev => !prev)}
                        >
                          <span className="text-gray-700 mr-2 transition-transform duration-200">
                            {createNutrientsCollapsed ? '▶' : '▼'}
                          </span>
                          <h3 className="text-lg font-medium text-gray-700 flex-grow">
                            {t('Nutrient Values')}
                          </h3>
                        </div>
                        
                        {!createNutrientsCollapsed && (
                          <>
                            {isLoadingNutrients ? (
                              <p className="text-gray-500 italic">{t('Loading nutrients...')}</p>
                            ) : enabledNutrients.length > 0 ? (
                              enabledNutrients.map(nutrient => (
                                <div key={nutrient.id} className="mb-4">
                                  <label htmlFor={`new-nutrient-${nutrient.id}`} className="block text-gray-700 text-sm font-bold mb-1">
                                    {nutrient.isFallback ? (
                                      <span className="italic font-normal text-gray-600">
                                        {nutrient.name}
                                        {nutrient.fallbackLanguage && 
                                          <span className="ml-1">({nutrient.fallbackLanguage})</span>
                                        }
                                      </span>
                                    ) : (
                                      nutrient.name
                                    )}
                                    {/* Display unit in parentheses */}
                                    {nutrient.unit && (
                                      <span className="text-xs ml-1 text-gray-500">({nutrient.unit})</span>
                                    )}
                                    {/* Add disabled indicator */}
                                    {!nutrient.isEnabled && (
                                      <span className="ml-2 text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full">
                                        {t('Disabled')}
                                      </span>
                                    )}
                                  </label>
                                  <input
                                    id={`new-nutrient-${nutrient.id}`}
                                    type="number"
                                    step="any"
                                    min="0"
                                    value={newNutrientValues[nutrient.id] ?? ''} // Use ?? here too for consistency
                                    onChange={(e) => handleNewNutrientValueChange(nutrient.id, e.target.value)}
                                    // Disable scroll wheel changing value
                                    onWheel={(e) => e.target.blur()} 
                                    placeholder={t('Enter numeric value')}
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              ))
                            ) : (
                              <p className="text-gray-500 italic">{t('No nutrients configured for this school.')}</p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Tag section - Only show for dishes */}
                    {activeTab === 'dishes' && (
                      <div className="space-y-4 mb-6 mt-6 pt-4 border-t">
                        <div 
                          className="flex items-center cursor-pointer mb-2 border-b pb-1" 
                          onClick={() => setCreateTagsCollapsed(prev => !prev)}
                        >
                          <span className="text-gray-700 mr-2 transition-transform duration-200">
                            {createTagsCollapsed ? '▶' : '▼'}
                          </span>
                          <h3 className="text-lg font-medium text-gray-700 flex-grow">
                            {t('Tags')}
                          </h3>
                        </div>
                        
                        {!createTagsCollapsed && (
                          <div className="mt-2">
                            <div className="mb-4">
                              <label className="flex items-center">
                                <input 
                                  type="checkbox" 
                                  checked={newNoTags}
                                  onChange={(e) => handleNewNoTagsChange(e.target.checked)}
                                  className="mr-2 h-4 w-4 text-blue-600"
                                />
                                <span>{t('No Tags in this Dish')}</span>
                              </label>
                            </div>
                            
                            {!newNoTags && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {isLoadingTags ? (
                                  <p>{t('Loading tags...')}</p>
                                ) : tags.length === 0 ? (
                                  <p className="text-amber-600">{t('No tags configured. Add tags in the Settings page first.')}</p>
                                ) : (
                                  tags.map(tag => (
                                    <label key={tag.id} className="flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={newSelectedTags.includes(tag.id)}
                                        onChange={() => handleNewTagChange(tag.id)}
                                        className="mr-2 h-4 w-4 text-blue-600 flex-shrink-0"
                                        disabled={newNoTags}
                                      />
                                      <span className="flex items-center">
                                        {tag.logo_path && (
                                          <img 
                                            src={getTagLogoUrl(tag.logo_path)} 
                                            alt={tag.name}
                                            className="h-6 w-6 mr-2 object-contain flex-shrink-0"
                                          />
                                        )}
                                        <span className="text-sm">{tag.name}</span>
                                      </span>
                                    </label>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <button
                    type="submit"
                    disabled={saving || isLoadingNutrients} // Disable while nutrients are loading
                    className="w-full bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                  >
                    {saving 
                      ? t('saving') 
                      : (activeTab === 'dishes' ? t('admin.createDish') : t('admin.createSpecialName'))}
                  </button>
                </form>
              </div>
            </div>
            
            {/* Delete Modal - Now using the reusable component */}
            <DeleteDishModal
              isOpen={deleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
              onSuccess={handleDeleteSuccess}
              itemId={selectedItemId}
              itemType={activeTab === 'dishes' ? 'dish' : 'specialName'}
              schoolId={getSchoolId()} // Use the helper function here
              language={i18n.language} // Pass current language
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default UnifiedTranslationPage; 